/**
 * =============================================================================
 * ===== FILE: race.js — ENGINE UTAMA BALAPAN =====
 * =============================================================================
 * State machine: idle → countdown → racing → (roundend) → finished
 * Mode: classic | sprint | marathon | survival | relay
 * Menggambar arena + semut + partikel setiap frame (~60 FPS)
 * Callback ke UI: onState, onHud, onFinish, onBanner
 * =============================================================================
 */

import { CONFIG, MODE_INFO, clamp, formatTime, shuffle as shuf } from './config.js';
import { Ant } from './ant.js';
import { Arena } from './arena.js';
import { Camera } from './camera.js';
import { ParticleSystem } from './particles.js';
import { audio } from './audio.js';

/** @typedef {'idle'|'countdown'|'racing'|'roundend'|'finished'} RaceState */

/**
 * RaceEngine — otak balapan (setup, loop, ranking, render).
 */
export class RaceEngine {
  /**
   * constructor(canvas)
   * Siapkan kamera, arena, partikel. Canvas final dipasang lewat attachCanvas.
   */
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas?.getContext?.('2d', { alpha: false, desynchronized: true }) || null;
    this.camera = new Camera();
    this.arena = new Arena();
    this.particles = new ParticleSystem();
    this.ants = [];
    this.state = /** @type {RaceState} */ ('idle');
    this.mode = 'classic';
    this.trackLength = CONFIG.DISTANCES.classic;
    this.elapsed = 0;
    this.countdown = 0;
    this.countdownLabel = '';
    this.dpr = 1;
    this.width = 800;
    this.height = 600;
    this.running = false;
    this.raf = 0;
    this.lastTs = 0;
    this.results = [];
    this.onState = null;
    this.onHud = null;
    this.onFinish = null;
    this.onBanner = null;

    /* Survival / Relay state */
    this.alive = [];
    this.elimTimer = 0;
    this.relayRound = 0;
    this.relayScores = new Map();
    this.finishOrder = [];
    this._relayTimer = 0;
    this._finishHold = 0;

    this._boundLoop = this._loop.bind(this);
  }

  /** Pasang #game-canvas dari UI dan resize. */
  attachCanvas(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    this.resize();
  }

  /** Daftarkan callback ke UIController. */
  setCallbacks({ onState, onHud, onFinish, onBanner }) {
    this.onState = onState;
    this.onHud = onHud;
    this.onFinish = onFinish;
    this.onBanner = onBanner;
  }

  /** Sesuaikan canvas + DPR + layout lane saat window berubah. */
  resize() {
    if (!this.canvas) return;
    const parent = this.canvas.parentElement || document.body;
    const rect = parent.getBoundingClientRect();
    this.width = Math.max(320, rect.width || window.innerWidth || 800);
    this.height = Math.max(240, rect.height || window.innerHeight || 600);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    if (this.ctx) this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.camera.resize(this.width, this.height);
    this.arena.height = this.height;
    for (const a of this.ants) a.setTrackLayout(this.height, this.ants.length, this.width);
  }

  /**
   * setup(participants, mode)
   * Buat entity Ant dari data menu, set panjang lintasan, reset state.
   */
  setup(participants, mode) {
    this._clearTimers();
    this.mode = mode;
    this.trackLength = CONFIG.DISTANCES[mode] || CONFIG.DISTANCES.classic;
    this.ants = participants.map(
      (p, i) =>
        new Ant(
          {
            id: p.id,
            name: p.name,
            skinId: p.skinId,
            color: p.color,
            points: this.relayScores.get(p.id) || 0,
          },
          i,
          participants.length
        )
    );
    for (const a of this.ants) a.setTrackLayout(this.height, this.ants.length, this.width);
    this.arena.laneCount = this.ants.length;
    this.arena.setTrack(this.trackLength, this.height);
    this.particles.clear();
    this.elapsed = 0;
    this.results = [];
    this.finishOrder = [];
    this.alive = this.ants.slice();
    this.elimTimer = CONFIG.SURVIVAL_ELIM_INTERVAL;
    this._finishHold = 0;
    this.state = 'idle';
    this.camera.setImmediate(0);
    this._emitState();
  }

  /** Mulai countdown 3-2-1-GO (mode non-relay). */
  start() {
    if (!this.ants.length) return;
    this._clearTimers();
    for (const a of this.ants) a.resetPosition();
    this.elapsed = 0;
    this.results = [];
    this.finishOrder = [];
    this.alive = this.ants.filter((a) => !a.eliminated);
    this.elimTimer = CONFIG.SURVIVAL_ELIM_INTERVAL;
    this.countdown = 3.25;
    this.countdownLabel = '3';
    this._finishHold = 0;
    this.state = 'countdown';
    this.camera.setImmediate(0);
    this.camera.addShake(2);
    this.particles.clear();
    this._emitState();
    this._ensureLoop();
    audio.countdownBeep(3);
  }

  /** Mulai turnamen relay: reset skor + ronde 1. */
  startRelayTournament(participants) {
    this.relayRound = 0;
    this.relayScores = new Map();
    participants.forEach((p) => this.relayScores.set(p.id, 0));
    this.setup(participants, 'relay');
    this._beginRelayRound();
  }

  /** Internal: acak lane, reset, countdown untuk ronde relay. */
  _beginRelayRound() {
    this._clearTimers();
    this.relayRound++;
    const order = shuf(this.ants);
    order.forEach((a, i) => {
      a.laneIndex = i;
      a.resetPosition();
      a.eliminated = false;
      a.rank = 0;
      a.setTrackLayout(this.height, this.ants.length, this.width);
    });
    this.ants = order;
    this.alive = this.ants.slice();
    this.finishOrder = [];
    this.results = [];
    this.elapsed = 0;
    this.elimTimer = 0;
    this.countdown = 3.25;
    this.countdownLabel = '3';
    this._finishHold = 0;
    this.state = 'countdown';
    this.camera.setImmediate(0);
    this.arena.laneCount = this.ants.length;
    this.particles.clear();
    this._emitState();
    this.onBanner?.(`Relay Ronde ${this.relayRound}/${CONFIG.RELAY_ROUNDS}`);
    this._ensureLoop();
    audio.countdownBeep(3);
  }

  /** Hentikan loop (keluar ke menu). */
  stop() {
    this._clearTimers();
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.state = 'idle';
    this.countdownLabel = '';
    this._emitState();
  }

  /** Bersihkan timeout antar-ronde relay. */
  _clearTimers() {
    if (this._relayTimer) {
      clearTimeout(this._relayTimer);
      this._relayTimer = 0;
    }
  }

  /**
   * skipToEnd()
   * Fast-forward simulasi RNG sampai finish (tombol Lewati).
   * Instant fair finish via simulated RNG steps
   */
  skipToEnd() {
    if (this.state === 'countdown') {
      this.countdown = 0;
      this.countdownLabel = '';
      this.state = 'racing';
      this._emitState();
    }
    if (this.state !== 'racing') return;

    let guard = 0;
    while (this.state === 'racing' && guard < 8000) {
      this._update(0.08);
      guard++;
    }
  }

  /** Pastikan requestAnimationFrame loop aktif. */
  _ensureLoop() {
    if (this.running) return;
    this.running = true;
    this.lastTs = performance.now();
    this.raf = requestAnimationFrame(this._boundLoop);
  }

  /** Satu frame: dt → update → draw. */
  _loop(ts) {
    if (!this.running) return;
    let dt = (ts - this.lastTs) / 1000;
    this.lastTs = ts;
    if (!Number.isFinite(dt) || dt < 0) dt = 0.016;
    dt = Math.min(dt, CONFIG.MAX_DT);

    this._update(dt);
    this._draw();

    if (this.state === 'finished') {
      this._finishHold += dt;
    }

    this.raf = requestAnimationFrame(this._boundLoop);
  }

  /**
   * _update(dt)
   * Logika per state: countdown, gerak RNG, survival, cek finish.
   */
  _update(dt) {
    this.arena.update(dt);
    this.particles.update(dt);

    /* ----- COUNTDOWN 3-2-1-GO ----- */
    if (this.state === 'countdown') {
      const prev = Math.ceil(this.countdown);
      this.countdown -= dt;
      const cur = Math.ceil(this.countdown);
      if (this.countdown > 1) {
        this.countdownLabel = String(Math.max(1, cur));
        if (cur < prev && cur >= 1) audio.countdownBeep(cur);
      } else if (this.countdown > 0) {
        if (this.countdownLabel !== 'GO!') {
          this.countdownLabel = 'GO!';
          audio.countdownBeep(0);
          audio.startGun();
          this.camera.addShake(14);
        }
      } else {
        this.state = 'racing';
        this.countdownLabel = '';
        this._emitState();
        audio.cheer();
      }
      for (const a of this.ants) a.updateAnim(dt, 0);
      this.camera.followLeader(40, this.trackLength);
      this.camera.update(dt);
      this._emitHud();
      return;
    }

    /* ----- RACING ----- */
    if (this.state === 'racing') {
      this.elapsed += dt;

      for (const a of this.ants) {
        a.updateRace(dt, this.trackLength, !a.eliminated);
        if (a.finished && a.finishTime == null) {
          a.finishTime = this.elapsed;
          this.finishOrder.push(a);
          a.rank = this.finishOrder.length;
        }
      }

      /* Mode Survival: gugurkan paling belakang tiap interval */
      if (this.mode === 'survival') {
        this.elimTimer -= dt;
        const still = this.ants.filter((a) => !a.eliminated && !a.finished);
        if (still.length > 1 && this.elimTimer <= 0) {
          still.sort((a, b) => a.x - b.x);
          const victim = still[0];
          victim.eliminated = true;
          const remaining = this.ants.filter((a) => !a.eliminated).length;
          victim.rank = remaining + 1;
          audio.eliminate();
          this.onBanner?.(`${victim.name} tersingkir!`);
          this.elimTimer = CONFIG.SURVIVAL_ELIM_INTERVAL * (0.85 + Math.random() * 0.3);
          this.camera.addShake(6);
          this.particles.spawnDust(victim.x, victim.y, 8, 'rgba(239,71,111,0.5)');
        }

        const alive = this.ants.filter((a) => !a.eliminated);
        if (alive.length <= 1) {
          const winner = alive[0] || this.ants.slice().sort((a, b) => b.x - a.x)[0];
          if (winner && !winner.finished) {
            winner.finished = true;
            winner.finishTime = this.elapsed;
            winner.rank = 1;
            if (!this.finishOrder.includes(winner)) this.finishOrder.unshift(winner);
          }
          this._completeRace();
        } else if (alive.every((a) => a.finished)) {
          this._completeRace();
        }
      } else {
        /* Mode normal: selesai jika semua sudah finish */
        const active = this.ants.filter((a) => !a.eliminated);
        if (active.length && active.every((a) => a.finished)) {
          this._completeRace();
        }
      }

      /* Kamera ikut pemimpin */
      let leaderX = 0;
      for (const a of this.ants) {
        if (!a.eliminated && a.x > leaderX) leaderX = a.x;
      }
      this.camera.followLeader(leaderX, this.trackLength);
      this.camera.update(dt);
      this._emitHud();
      return;
    }

    /* ----- SELESAI / ANTAR RONDE: animasi idle ----- */
    if (this.state === 'roundend' || this.state === 'finished') {
      this.camera.update(dt);
      for (const a of this.ants) {
        if (!a.eliminated) a.updateAnim(dt, a.finished ? 8 : 0);
      }
      return;
    }

    this.camera.update(dt);
    for (const a of this.ants) a.updateAnim(dt, 12);
  }

  /**
   * _completeRace()
   * Susun ranking / poin relay, confetti, panggil onFinish (atau next round).
   */
  _completeRace() {
    if (this.state === 'finished' || this.state === 'roundend') return;

    const ordered = this.ants.slice().sort((a, b) => {
      if (a.eliminated !== b.eliminated) return a.eliminated ? 1 : -1;
      if (a.finished && b.finished) return (a.finishTime ?? 9e9) - (b.finishTime ?? 9e9);
      if (a.finished !== b.finished) return a.finished ? -1 : 1;
      return b.x - a.x;
    });

    ordered.forEach((a, i) => {
      if (!a.rank) a.rank = i + 1;
      if (a.finished && a.finishTime == null) a.finishTime = this.elapsed;
    });

    this.results = ordered.map((a) => ({
      id: a.id,
      name: a.name,
      skinId: a.skinId,
      color: a.color,
      rank: a.rank,
      time: a.finishTime,
      eliminated: a.eliminated,
      points: a.points,
    }));

    /* ----- RELAY: akumulasi poin multi-ronde ----- */
    if (this.mode === 'relay') {
      const finishers = ordered.filter((a) => !a.eliminated);
      finishers.forEach((a, i) => {
        const pts = CONFIG.RELAY_POINTS[Math.min(i, CONFIG.RELAY_POINTS.length - 1)] ?? 1;
        const prev = this.relayScores.get(a.id) || 0;
        this.relayScores.set(a.id, prev + pts);
        a.points = prev + pts;
      });

      this.results = ordered.map((a) => ({
        id: a.id,
        name: a.name,
        skinId: a.skinId,
        color: a.color,
        rank: a.rank,
        time: a.finishTime,
        eliminated: a.eliminated,
        points: this.relayScores.get(a.id) || 0,
        roundPoints:
          CONFIG.RELAY_POINTS[Math.min((a.rank || 1) - 1, CONFIG.RELAY_POINTS.length - 1)] ?? 1,
      }));

      if (this.relayRound < CONFIG.RELAY_ROUNDS) {
        this.state = 'roundend';
        this._emitState();
        audio.finish();
        this.particles.burstConfetti(this.width * 0.5, this.height * 0.3, 90);
        this.onBanner?.(`Akhir Ronde ${this.relayRound} — lanjut...`);
        this._relayTimer = window.setTimeout(() => {
          if (
            this.mode === 'relay' &&
            this.relayRound < CONFIG.RELAY_ROUNDS &&
            this.state === 'roundend'
          ) {
            this._beginRelayRound();
          }
        }, 2600);
        return;
      }

      this.results.sort((a, b) => b.points - a.points || (a.time ?? 999) - (b.time ?? 999));
      this.results.forEach((r, i) => {
        r.rank = i + 1;
      });
    }

    this.results.sort((a, b) => a.rank - b.rank);
    if (this.mode !== 'relay') {
      this.results.forEach((r, i) => {
        r.rank = i + 1;
      });
    }

    this.state = 'finished';
    this._finishHold = 0;
    this._emitState();
    audio.finish();
    this.particles.burstConfetti(this.width * 0.5, this.height * 0.35, 150);
    this.camera.addShake(10);
    this.onFinish?.(this.results, {
      mode: this.mode,
      elapsed: this.elapsed,
      relayRound: this.relayRound,
    });
  }

  /**
   * _draw()
   * Render frame: arena → semut (urut Y) → debu → confetti → speed lines.
   */
  _draw() {
    const ctx = this.ctx;
    if (!ctx || !this.canvas) return;
    const w = this.width;
    const h = this.height;

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    /* World space (ikuti kamera) */
    ctx.save();
    this.camera.apply(ctx);
    this.arena.draw(ctx, this.camera);

    // Visual leader tag: first finisher if anyone finished, else furthest racer
    let leader = null;
    const finishers = this.ants.filter((a) => !a.eliminated && a.finished);
    if (finishers.length) {
      leader = finishers.slice().sort((a, b) => (a.finishTime ?? 0) - (b.finishTime ?? 0))[0];
    } else {
      let leaderX = -Infinity;
      for (const a of this.ants) {
        if (!a.eliminated && a.x > leaderX) {
          leaderX = a.x;
          leader = a;
        }
      }
    }
    const camMid = this.camera.x + w * 0.45;
    const sorted = this.ants.slice().sort((a, b) => a.y - b.y);
    for (const a of sorted) {
      a.draw(ctx, this.particles, this.camera, {
        isLeader: a === leader,
        nearCam: Math.abs(a.x - camMid) < w * 0.42,
        showTag: true,
      });
    }
    this.particles.drawDust(ctx, this.camera.x, w);
    ctx.restore();

    /* Screen space */
    this.particles.drawConfetti(ctx);

    /* Motion lines ringan saat kecepatan tinggi */
    if (this.state === 'racing') {
      let maxSp = 0;
      for (const a of this.ants) if (!a.eliminated) maxSp = Math.max(maxSp, a.speed);
      if (maxSp > 180) {
        ctx.fillStyle = `rgba(255,255,255,${clamp((maxSp - 180) / 400, 0, 0.06)})`;
        for (let i = 0; i < 8; i++) {
          const y = (i * 97 + this.elapsed * 200) % h;
          ctx.fillRect(0, y, w, 1.5);
        }
      }
    }

    ctx.restore();
  }

  /** Beritahu UI state berubah. */
  _emitState() {
    this.onState?.(this.state, this.mode);
  }

  /**
   * _emitHud()
   * Leaderboard live: finisher permanen by waktu, lalu yang masih balapan by jarak.
   */
  _emitHud() {
    if (!this.onHud) return;

    // Standing order:
    // 1) Finished ants keep permanent place by finishTime (1st finisher stays #1)
    // 2) Still-racing ants ranked by distance (x), after all finishers
    const standing = this.ants
      .filter((a) => !a.eliminated)
      .slice()
      .sort((a, b) => {
        if (a.finished && b.finished) {
          const ta = a.finishTime ?? Number.POSITIVE_INFINITY;
          const tb = b.finishTime ?? Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
          // stable fallback: earlier rank / id
          return (a.rank || 0) - (b.rank || 0) || a.id - b.id;
        }
        if (a.finished !== b.finished) return a.finished ? -1 : 1;
        if (b.x !== a.x) return b.x - a.x;
        return a.id - b.id;
      });

    const board = standing.slice(0, 5).map((a, i) => ({
      pos: a.finished && a.rank ? a.rank : i + 1,
      name: a.name,
      color: a.color,
      progress: a.progress,
      finished: !!a.finished,
      points: this.relayScores.get(a.id) || a.points || 0,
    }));

    // Re-number display positions 1..n from standing order (finish ranks already correct)
    board.forEach((row, i) => {
      row.pos = i + 1;
    });

    let leaderProgress = 0;
    for (const a of this.ants) {
      if (!a.eliminated) leaderProgress = Math.max(leaderProgress, a.progress);
    }

    this.onHud({
      elapsed: this.elapsed,
      leaderProgress,
      board,
      countdown: this.countdownLabel,
      mode: this.mode,
      modeLabel: MODE_INFO[this.mode]?.label || this.mode,
      alive: this.ants.filter((a) => !a.eliminated).length,
      total: this.ants.length,
      relayRound: this.relayRound,
      timeText: formatTime(this.elapsed),
    });
  }

  /** Cleanup total. */
  destroy() {
    this.stop();
  }
}

export { formatTime };
